/**
 * Dinheiro é sempre representado em centavos inteiros (number seguro até 2^53).
 * Nunca use float para cálculo; converta texto/decimal para centavos na borda.
 * Arredondamento: meio para cima (half-up) na 3ª casa decimal, aplicado apenas na conversão.
 */

export type Cents = number;

export const MAX_CENTS = 99_999_999_999_99; // R$ 99 bilhões

export function isCents(value: unknown): value is Cents {
  return typeof value === 'number' && Number.isSafeInteger(value) && Math.abs(value) <= MAX_CENTS;
}

/**
 * Converte texto em pt-BR ("1.234,56", "R$ 10", "-3,5") ou decimal ("1234.56") para centavos.
 * Retorna null para vazio ou inválido — ausência de valor NÃO vira zero.
 */
export function parseMoney(input: string | number | null | undefined): Cents | null {
  if (input === null || input === undefined) return null;
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return null;
    return decimalStringToCents(input.toFixed(6));
  }
  let s = input.trim().replace(/\s|R\$/g, '');
  if (s === '') return null;
  const negative = s.startsWith('-') || (s.startsWith('(') && s.endsWith(')'));
  s = s.replace(/^[-(]|\)$/g, '');
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && hasDot) {
    // O último separador é o decimal.
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (hasComma) {
    s = s.replace(',', '.');
  } else if (hasDot) {
    // "1.234" (milhar pt-BR) vs "12.5" (decimal): 3 dígitos após um único ponto = milhar.
    const parts = s.split('.');
    if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3)) s = parts.join('');
  }
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const cents = decimalStringToCents(s);
  if (cents === null) return null;
  return negative ? -cents : cents;
}

function decimalStringToCents(s: string): Cents | null {
  const negative = s.startsWith('-');
  const clean = negative ? s.slice(1) : s;
  const [intPart, fracRaw = ''] = clean.split('.');
  if (!/^\d+$/.test(intPart)) return null;
  const frac = (fracRaw + '000').slice(0, 3);
  let cents = Number(intPart) * 100 + Number(frac.slice(0, 2));
  if (Number(frac[2]) >= 5) cents += 1;
  if (!isCents(cents)) return null;
  return negative ? -cents : cents;
}

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const plain = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Formata centavos como "R$ 1.234,56". null → "—" (valor não informado). */
export function formatMoney(cents: Cents | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return brl.format(cents / 100);
}

/** "1.234,56" sem símbolo, para campos de entrada. */
export function formatMoneyPlain(cents: Cents | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return plain.format(cents / 100);
}

/** Valor para CSV/planilha: "1234,56" (pt-BR, sem milhar). */
export function formatMoneyCsv(cents: Cents | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)},${String(abs % 100).padStart(2, '0')}`;
}

export function sumCents(values: Array<Cents | null | undefined>): Cents {
  let total = 0;
  for (const v of values) if (typeof v === 'number') total += v;
  return total;
}

/** Divide um total em N parcelas inteiras; o resto em centavos vai para a primeira parcela. */
export function splitInstallments(total: Cents, count: number): Cents[] {
  if (!Number.isInteger(count) || count < 1) throw new Error('Quantidade de parcelas inválida');
  const base = Math.trunc(total / count);
  const remainder = total - base * count;
  return Array.from({ length: count }, (_, i) => (i === 0 ? base + remainder : base));
}
